import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Auth0Provider } from "@auth0/auth0-react";
import { ToastContainer } from "react-toastify";
import { AuthTokenProvider } from "../context/AuthTokenContext";
import { TranslationProvider } from "@/components/providers/TranslationProvider";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: "always",
    },
    mutations: {
      retry: 1,
    },
  },
});

// Enable background refetching
queryClient.getQueryCache().subscribe((event) => {
  if (event?.query?.state?.status === "error") {
    console.error("Query error:", event.query.state.error);
  }
});

export function Providers({ children }) {
  const navigate = useNavigate();
  
  // Get Auth0 config values from Vite env
  const domain = import.meta.env.VITE_AUTH0_DOMAIN || "";
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || "";
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE || "";
  const redirectUri = window.location.origin;

  const onRedirectCallback = (appState) => {
    // Navigate to the returnTo path or default to home
    navigate(appState?.returnTo || "/", { replace: true });
  };

  // Build authorization params - only include audience if it's a custom API (not Auth0 Management API)
  const authorizationParams = {
    redirect_uri: redirectUri,
  };
  
  // Only add audience if it's set and not the Management API
  if (audience && !audience.includes('/api/v2/')) {
    authorizationParams.audience = audience;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AuthTokenProvider>
        <QueryClientProvider client={queryClient}>
          <TranslationProvider>
            {children}
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </TranslationProvider>
        </QueryClientProvider>
      </AuthTokenProvider>
    </Auth0Provider>
  );
}
